import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { validateRouteRole } from '@/lib/routing.js';
import { instantFromDate, instantToDate, isAirAdmin } from '@airreading/common';

import { InfoPage } from '@/components/info-page.js';
import { SessionDateTimeDivider } from '@/components/session/session-date-time-divider.js';
import { EntityTableGroupBy, EntityTableInfiniteScroll } from '@/components/table/entity-table.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { TableMoreFiltersMenu } from '@/components/table/table-more-filters-menu.js';
import { Button } from '@/components/ui/button.js';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu.js';
import { Label } from '@/components/ui/label.js';
import { LoaderIcon } from '@/components/ui/loader-icon.js';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group.js';
import { lessonTypeaheadSearch, useOrgOptions, useSchoolOptions } from '@/lib/api-call-hooks.js';
import { EmptyArray, getAdminSessions } from '@/lib/api-calls.js';
import { useBoundedInfiniteRefresh, useInfiniteScrollQuery, uniqueById } from '@/lib/infinite-query-utils.js';
import { VisibleRowRange } from '@/components/table/use-visible-row-range.js';
import { useRowsBulkSelection } from '@/lib/bulk-selection.js';
import { useErrorToast } from '@/lib/error-handling.js';
import { usePaginatedFilters } from '@/lib/filtering.js';
import { classTimeTypesFilterOption } from '@/lib/form-utils.js';
import { INFINITE_SCROLL_PAGE_SIZE, pageSizeSchemaWithMax, paginationRequestSchema } from '@/lib/pagination.js';
import { pluralize } from '@/lib/string-utils.js';
import { getStudentYearOptions } from '@/lib/student-utils.js';
import { groupByStartDateAndTimeRange, StartDateTimeRangeGroupKey } from '@/lib/table-grouping-utils.js';
import { BulkRefreshZoomLinks } from '@/routes/_layout/sessions/-components/bulk-refresh-zoom-links.js';
import { CancelSessions } from '@/routes/_layout/sessions/-components/cancel-sessions.js';
import { CreateSessionModal } from '@/routes/_layout/sessions/-components/create-session-modal.js';
import { RestoreSessions } from '@/routes/_layout/sessions/-components/restore-sessions.js';
import { SessionsTable } from '@/routes/_layout/sessions/-components/sessions-table.js';
import { sessionsTabs } from '@/routes/_layout/sessions/-components/tabs.js';
import { FrequentTimeZones } from '@airreading/common';
import {
  AdminSession,
  classTimeTypeSchema,
  CoachSessionRemarkV2LabelMap,
  coachSessionRemarkV2Schema,
  LARGE_MAX_PAGE_SIZE,
  lessonKindSchema,
  LessonKindToLabel,
  studentYearSchema,
} from '@airreading/common/data';
import { useFeatureFlag } from '@airreading/feature-flags/web';

export const DEFAULT_LIVE_SESSION_PAGE_SIZE = 100;

const liveSessionPaginationRequestSchema = paginationRequestSchema.merge(
  z.object({
    pageSize: pageSizeSchemaWithMax(LARGE_MAX_PAGE_SIZE, DEFAULT_LIVE_SESSION_PAGE_SIZE),
  }),
);

export const Route = createFileRoute('/_layout/sessions/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: SessionsIndex,
  validateSearch: liveSessionPaginationRequestSchema.extend({
    query: z.string().optional(),
    orgs: z.string().array().optional(),
    schools: z.string().array().optional(),
    grades: z.array(studentYearSchema).optional(),
    lessons: z.string().array().optional(),
    attentionSessions: z.boolean().optional(),
    nextHour: z.boolean().optional(),
    lessonKinds: z.array(lessonKindSchema).optional(),
    classTypes: z.array(classTimeTypeSchema).optional(),
    sessionRemarksV2: z.array(coachSessionRemarkV2Schema).optional(),
  }),
});

const TodaySessionsViewMode = {
  All: 'all',
  Attention: 'attention',
  Current: 'current',
  SupportAdmin: 'supportAdmin',
  GoogleMeet: 'googleMeet',
} as const;

type TodaySessionsViewMode = (typeof TodaySessionsViewMode)[keyof typeof TodaySessionsViewMode];

const TODAY_SESSIONS_PAGE_PREFIX = 'sessions-today';

const TODAY_SESSIONS_FILTER_OPTIONS: {
  value: Exclude<TodaySessionsViewMode, typeof TodaySessionsViewMode.All>;
  id: string;
  label: string;
  testId: string;
}[] = [
  {
    value: TodaySessionsViewMode.Attention,
    id: 'attention-sessions',
    label: 'Attention sessions',
    testId: `${TODAY_SESSIONS_PAGE_PREFIX}-filter-radio-attention`,
  },
  {
    value: TodaySessionsViewMode.Current,
    id: 'current-sessions',
    label: 'Current sessions',
    testId: `${TODAY_SESSIONS_PAGE_PREFIX}-filter-radio-current`,
  },
  {
    value: TodaySessionsViewMode.SupportAdmin,
    id: 'support-admin-sessions',
    label: 'Support admin',
    testId: `${TODAY_SESSIONS_PAGE_PREFIX}-filter-radio-support-admin`,
  },
  {
    value: TodaySessionsViewMode.GoogleMeet,
    id: 'google-meet-sessions',
    label: 'Google Meet',
    testId: `${TODAY_SESSIONS_PAGE_PREFIX}-filter-radio-google-meet`,
  },
];

const getAdminSessionsArray = (result: { adminSessions: AdminSession[] }) => result.adminSessions;

function isTodaySessionsFilterValue(
  value: string,
): value is Exclude<TodaySessionsViewMode, typeof TodaySessionsViewMode.All> {
  return TODAY_SESSIONS_FILTER_OPTIONS.some((o) => o.value === value);
}

function useSessionsIndexModel() {
  const queryClient = useQueryClient();

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[query, activeQuery], setQuery],
      orgs: [[selectedOrgs, activeSelectedOrgs], setSelectedOrgs],
      schools: [[selectedSchools, activeSelectedSchools], setSelectedSchools],
      lessonKinds: [[selectedLessonKinds, activeSelectedLessonKinds], setSelectedLessonKinds],
      grades: [[selectedGrades, activeSelectedGrades], setSelectedGrades],
      lessons: [[selectedLessons, activeSelectedLessons], setSelectedLessons],
      attentionSessions: [[isAttentionSessionsSelected], setIsAttentionSessionsSelected],
      nextHour: [[isNextHourSelected], setIsNextHourSelected],
      classTypes: [[selectedClassTypes, activeSelectedClassTypes], setSelectedClassTypes],
      sessionRemarksV2: [[selectedSessionRemarksV2, activeSelectedSessionRemarksV2], setSelectedSessionRemarksV2],
    },
    resetFilters,
    activeFiltersCount,
  } = usePaginatedFilters(
    Route.id,
    [
      'query',
      'orgs',
      'schools',
      'grades',
      'lessons',
      'attentionSessions',
      'nextHour',
      'lessonKinds',
      'classTypes',
      'sessionRemarksV2',
    ],
    ['orgs', 'schools', 'classTypes', 'grades', 'lessons', 'lessonKinds', 'sessionRemarksV2'],
  );

  const initialViewMode: TodaySessionsViewMode = isAttentionSessionsSelected
    ? TodaySessionsViewMode.Attention
    : isNextHourSelected
      ? TodaySessionsViewMode.Current
      : TodaySessionsViewMode.All;
  const [viewMode, setViewMode] = useState<TodaySessionsViewMode>(initialViewMode);

  const applyViewMode = useCallback(
    (next: TodaySessionsViewMode) => {
      setViewMode(next);
      setIsAttentionSessionsSelected(next === TodaySessionsViewMode.Attention);
      setIsNextHourSelected(next === TodaySessionsViewMode.Current);
    },
    [setIsAttentionSessionsSelected, setIsNextHourSelected],
  );

  const dateRange = useMemo(() => {
    const today = Temporal.Now.plainDateISO(FrequentTimeZones.UsCentral);
    const startOfToday = today.toZonedDateTime(FrequentTimeZones.UsCentral).startOfDay().toInstant();
    const endOfToday = today.toZonedDateTime(FrequentTimeZones.UsCentral).add({ days: 1 }).startOfDay().toInstant();

    if (isNextHourSelected) {
      const now = Temporal.Now.instant();
      const next30Minutes = instantToDate(now.add({ minutes: 30 }));
      return {
        startOnOrAfter: now.toString(),
        startOnOrBefore: instantFromDate(next30Minutes).toString(),
        endOnOrAfter: now.toString(),
      };
    }

    return {
      startOnOrAfter: startOfToday.toString(),
      startOnOrBefore: endOfToday.toString(),
      endOnOrAfter: undefined,
    };
  }, [isNextHourSelected]);

  const requestQuery = useCallback(
    (nextPageIndex: number, nextPageSize: number) =>
      getAdminSessions({
        pageIndex: nextPageIndex,
        pageSize: nextPageSize,
        filters: {
          startOnOrAfter: dateRange.startOnOrAfter,
          startOnOrBefore: dateRange.startOnOrBefore,
          endOnOrAfter: dateRange.endOnOrAfter,
          query: activeQuery,
          orgs: activeSelectedOrgs,
          schools: activeSelectedSchools,
          grades: activeSelectedGrades,
          lessons: activeSelectedLessons,
          attentionSessions: isAttentionSessionsSelected,
          nextHour: isNextHourSelected,
          supportAdminSessions: viewMode === TodaySessionsViewMode.SupportAdmin,
          googleMeetSessions: viewMode === TodaySessionsViewMode.GoogleMeet,
          lessonKinds: activeSelectedLessonKinds,
          classTypes: activeSelectedClassTypes,
          sessionRemarksV2: activeSelectedSessionRemarksV2,
        },
        // When filtering by Google Meet sessions only: unchecked first within same class time (for ops live monitoring).
        sortBy:
          viewMode === TodaySessionsViewMode.GoogleMeet ? ['start-asc', 'googleMeetCheckedAt-asc'] : ['start-asc'],
      }),
    [
      dateRange.startOnOrAfter,
      dateRange.startOnOrBefore,
      dateRange.endOnOrAfter,
      activeQuery,
      activeSelectedOrgs,
      activeSelectedSchools,
      activeSelectedGrades,
      activeSelectedLessons,
      isAttentionSessionsSelected,
      isNextHourSelected,
      viewMode,
      activeSelectedLessonKinds,
      activeSelectedClassTypes,
      activeSelectedSessionRemarksV2,
    ],
  );

  const allSchoolOptions = useSchoolOptions();
  const allOrgOptions = useOrgOptions();

  const topBarRightComponents = (
    <CreateSessionModal
      trigger={<Button data-testid={`${TODAY_SESSIONS_PAGE_PREFIX}-table-button-new-session`}>New session</Button>}
    />
  );

  const displayTimeZone = FrequentTimeZones.UsCentral;

  const sessionGroupBy = useMemo<EntityTableGroupBy<AdminSession, StartDateTimeRangeGroupKey>>(
    () => ({
      getGroupKey: (row) => groupByStartDateAndTimeRange(row.startInstant, row.endInstant, displayTimeZone),
      renderDivider: (groupKey, rowCount) => <SessionDateTimeDivider groupKey={groupKey} rowCount={rowCount} />,
    }),
    [displayTimeZone],
  );

  return {
    queryClient,
    pageIndex,
    pageSize,
    onPaginationChange,
    requestQuery,
    query,
    setQuery,
    selectedOrgs,
    setSelectedOrgs,
    allOrgOptions,
    selectedSchools,
    setSelectedSchools,
    allSchoolOptions,
    selectedGrades,
    setSelectedGrades,
    selectedLessons,
    setSelectedLessons,
    selectedLessonKinds,
    setSelectedLessonKinds,
    selectedClassTypes,
    setSelectedClassTypes,
    selectedSessionRemarksV2,
    setSelectedSessionRemarksV2,
    activeFiltersCount,
    resetFilters,
    viewMode,
    applyViewMode,
    topBarRightComponents,
    displayTimeZone,
    sessionGroupBy,
  };
}

type SessionsIndexModel = ReturnType<typeof useSessionsIndexModel>;

function useSessionsFilterLeftComponents(model: SessionsIndexModel, showLoadingIndicator: boolean) {
  return useMemo(
    () => (
      <>
        <TableFilterInputField
          className="w-[200px] lg:w-[300px] xl:w-[350px] 2xl:w-[400px]"
          placeholder="Search sessions by student, teacher, email or lesson name"
          data-testid={`${TODAY_SESSIONS_PAGE_PREFIX}-filter-input-search`}
          value={model.query ?? ''}
          onChange={(event) => {
            model.setQuery(event.target.value);
          }}
        />

        <RadioGroup
          className="flex items-center gap-4 w-min"
          value={model.viewMode === TodaySessionsViewMode.All ? '' : model.viewMode}
          onValueChange={(value) => {
            if (isTodaySessionsFilterValue(value)) model.applyViewMode(value);
          }}
        >
          {TODAY_SESSIONS_FILTER_OPTIONS.map(({ value, id, label, testId }) => (
            <div
              key={value}
              className="flex items-center gap-2 cursor-pointer rounded-[6px] bg-white p-3"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                model.applyViewMode(model.viewMode === value ? TodaySessionsViewMode.All : value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  model.applyViewMode(model.viewMode === value ? TodaySessionsViewMode.All : value);
                }
              }}
              role="button"
              tabIndex={0}
              aria-pressed={model.viewMode === value}
              aria-label={label}
            >
              <RadioGroupItem value={value} id={id} data-testid={testId} />
              <Label htmlFor={id} className="block">
                {label}
              </Label>
            </div>
          ))}
        </RadioGroup>

        <TableMoreFiltersMenu
          triggerTestId={`${TODAY_SESSIONS_PAGE_PREFIX}-filter-button-more-filters`}
          filterItems={[
            {
              title: 'District',
              filterComponent: (
                <TableFilterDropdown
                  title="District"
                  options={model.allOrgOptions}
                  selectedValues={model.selectedOrgs ?? []}
                  onChange={model.setSelectedOrgs}
                />
              ),
            },
            {
              title: 'School',
              filterComponent: (
                <TableFilterDropdown
                  title="School"
                  options={model.allSchoolOptions}
                  selectedValues={model.selectedSchools ?? []}
                  onChange={model.setSelectedSchools}
                />
              ),
            },
            {
              title: 'Class type',
              filterComponent: (
                <TableFilterDropdown
                  title="Class type"
                  options={classTimeTypesFilterOption}
                  selectedValues={model.selectedClassTypes ?? []}
                  onChange={model.setSelectedClassTypes}
                />
              ),
            },
            {
              title: 'Grade',
              filterComponent: (
                <TableFilterDropdown
                  title="Grade"
                  options={getStudentYearOptions()}
                  selectedValues={model.selectedGrades ?? []}
                  onChange={model.setSelectedGrades}
                />
              ),
            },
            {
              title: 'Lesson type',
              filterComponent: (
                <TableFilterDropdown
                  title="Lesson type"
                  options={lessonKindSchema.options.map((type) => ({
                    label: LessonKindToLabel[type],
                    value: type,
                  }))}
                  selectedValues={model.selectedLessonKinds ?? []}
                  onChange={model.setSelectedLessonKinds}
                />
              ),
            },
            {
              title: 'Remarks',
              filterComponent: (
                <TableFilterDropdown
                  title="Remarks"
                  options={coachSessionRemarkV2Schema.options.map((type) => ({
                    label: CoachSessionRemarkV2LabelMap[type],
                    value: type,
                  }))}
                  selectedValues={model.selectedSessionRemarksV2 ?? []}
                  onChange={model.setSelectedSessionRemarksV2}
                />
              ),
            },
            {
              title: 'Lesson name',
              filterComponent: (
                <TableFilterDropdown
                  title="Lesson name"
                  options={lessonTypeaheadSearch(model.queryClient)}
                  selectedValues={model.selectedLessons ?? []}
                  onChange={model.setSelectedLessons}
                />
              ),
            },
          ]}
          activeFiltersCount={model.activeFiltersCount}
        />
        {showLoadingIndicator && <LoaderIcon className="h-4 w-4" />}
        <TableFilterResetButton
          show
          onClick={model.resetFilters}
          data-testid={`${TODAY_SESSIONS_PAGE_PREFIX}-filter-button-reset`}
        />
      </>
    ),
    [
      model.query,
      model.setQuery,
      model.viewMode,
      model.applyViewMode,
      model.allOrgOptions,
      model.selectedOrgs,
      model.setSelectedOrgs,
      model.allSchoolOptions,
      model.selectedSchools,
      model.setSelectedSchools,
      model.selectedGrades,
      model.setSelectedGrades,
      model.selectedLessons,
      model.setSelectedLessons,
      model.selectedLessonKinds,
      model.setSelectedLessonKinds,
      model.selectedClassTypes,
      model.setSelectedClassTypes,
      model.selectedSessionRemarksV2,
      model.setSelectedSessionRemarksV2,
      model.activeFiltersCount,
      model.queryClient,
      model.resetFilters,
      showLoadingIndicator,
    ],
  );
}

function SessionsIndexView({
  model,
  sessions,
  rowCount,
  isLoading,
  hasError,
  refetch,
  filterLeftComponents,
  paginationState,
  infiniteScroll,
}: {
  model: SessionsIndexModel;
  sessions: AdminSession[];
  rowCount: number;
  isLoading: boolean;
  hasError: boolean;
  refetch: () => void;
  filterLeftComponents: ReactNode;
  paginationState?: {
    pageIndex: number;
    pageSize: number;
    onPaginationChange: SessionsIndexModel['onPaginationChange'];
  };
  infiniteScroll?: EntityTableInfiniteScroll;
}): ReactNode {
  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<AdminSession>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => sessions.find((session) => session.id === id),
    });

  const bulkActionsMenuItems = (
    <>
      <CancelSessions
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Cancel session', 'Cancel sessions')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
      <RestoreSessions
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Restore session', 'Restore sessions')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
      <BulkRefreshZoomLinks
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Refresh zoom host link', 'Refresh zoom host links')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
    </>
  );

  return (
    <InfoPage headerText="Sessions" tabs={sessionsTabs()}>
      <SessionsTable
        sessions={sessions}
        hasError={hasError}
        isLoading={isLoading}
        errorState={<TableErrorState refetchAction={refetch} />}
        tableHookProps={{
          rowCount,
          state: {
            ...(paginationState
              ? { pagination: { pageIndex: paginationState.pageIndex, pageSize: paginationState.pageSize } }
              : {}),
            rowSelection,
          },
          onRowSelectionChange,
          getRowId,
          ...(paginationState ? { onPaginationChange: paginationState.onPaginationChange } : {}),
        }}
        displayTimeZone={model.displayTimeZone}
        filters={{ filterLeftComponents }}
        bulkActionsMenuItems={bulkActionsMenuItems}
        topBarRightComponents={model.topBarRightComponents}
        from="today"
        paginationOptions={paginationState ? [10, 20, 30, 40, 50, 100] : undefined}
        groupBy={model.sessionGroupBy}
        infiniteScroll={infiniteScroll}
      />
    </InfoPage>
  );
}

function PagedSessionsIndex() {
  const refreshIntervalMs = 5000;
  const model = useSessionsIndexModel();

  const {
    data: sessionsResp,
    isPending,
    error,
    refetch,
    isFetching,
  } = useQuery({
    ...model.requestQuery(model.pageIndex, model.pageSize),
    refetchInterval: refreshIntervalMs,
    refetchIntervalInBackground: false,
  });

  useErrorToast(error, 'Error loading sessions. Please try again.');

  const sessions = useMemo(() => sessionsResp?.adminSessions ?? EmptyArray, [sessionsResp]);
  const rowCount = sessionsResp?.total ?? 0;

  const filterLeftComponents = useSessionsFilterLeftComponents(model, isFetching && !isPending);

  return (
    <SessionsIndexView
      model={model}
      sessions={sessions}
      rowCount={rowCount}
      isLoading={isPending}
      hasError={!!error}
      refetch={refetch}
      filterLeftComponents={filterLeftComponents}
      paginationState={{
        pageIndex: model.pageIndex,
        pageSize: model.pageSize,
        onPaginationChange: model.onPaginationChange,
      }}
    />
  );
}

function InfiniteSessionsIndex() {
  const refreshIntervalMs = 5000;
  const model = useSessionsIndexModel();

  const {
    data: sessionsResp,
    queryKey: sessionsQueryKey,
    isPending,
    error,
    refetch,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteScrollQuery(model.requestQuery, getAdminSessionsArray, INFINITE_SCROLL_PAGE_SIZE);

  const [visibleRowRange, setVisibleRowRange] = useState<VisibleRowRange | null>(null);
  const visiblePageIndexes = useMemo(
    () =>
      visibleRowRange
        ? [
            Math.floor(visibleRowRange.first / INFINITE_SCROLL_PAGE_SIZE),
            Math.floor(visibleRowRange.last / INFINITE_SCROLL_PAGE_SIZE),
          ]
        : EmptyArray,
    [visibleRowRange],
  );

  useBoundedInfiniteRefresh({
    queryKey: sessionsQueryKey,
    requestQuery: model.requestQuery,
    pageSize: INFINITE_SCROLL_PAGE_SIZE,
    pageIndexes: visiblePageIndexes,
    intervalMs: refreshIntervalMs,
    enabled: !error,
  });

  useErrorToast(error, 'Error loading sessions. Please try again.');

  const sessions = useMemo(
    () => (sessionsResp ? uniqueById(sessionsResp.pages.flatMap((page) => page.adminSessions)) : EmptyArray),
    [sessionsResp],
  );
  const rowCount = sessionsResp?.pages[0]?.total ?? 0;

  const filterLeftComponents = useSessionsFilterLeftComponents(model, isFetching && !isPending && !isFetchingNextPage);

  return (
    <SessionsIndexView
      model={model}
      sessions={sessions}
      rowCount={rowCount}
      isLoading={isPending}
      hasError={!!error}
      refetch={refetch}
      filterLeftComponents={filterLeftComponents}
      infiniteScroll={{
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        pageSize: INFINITE_SCROLL_PAGE_SIZE,
        onVisibleRowRangeChange: setVisibleRowRange,
      }}
    />
  );
}

function SessionsIndex() {
  const infiniteScrollEnabled = useFeatureFlag('entity-table-infinite-scroll', false);
  return infiniteScrollEnabled ? <InfiniteSessionsIndex /> : <PagedSessionsIndex />;
}
